import { BADGE_ARTWORK } from "./badgeArtwork";

// A round badge icon assembled at runtime: the category's OWN colour as the circle, the icon's artwork in white on it. The colour is the one the category already has
// everywhere (its bubble, the banner of the drawer opened by clicking it, its tooltip); see badgeArtwork.ts for why it cannot be baked into the file.
const cache = new Map<string, string>();
export const COMPACT_BADGE = "unknown";

export const hasBadge = (name: string): boolean => name === COMPACT_BADGE || Object.prototype.hasOwnProperty.call(BADGE_ARTWORK, name);

/**
 * "Unknown" / "no value for this category" is NOT a glyph on a coloured disc: it is a DASHED RING with a WHITE, opaque inside (Chris, 2026-10-18: a question mark reads as a mystery to be
 * solved, and this means "nothing was recorded here"; and "I don't want to have a ring that is transparent in the middle"). The white disc fills the whole 64 px circle so nothing under the
 * icon (the bubble's own colour, a box, a ribbon) shows through, inside the ring or around it. 12 dashes: 12 x (9 + 6.446) is the circumference of r 29.5. The same drawing as
 * assets/images/visitor_analytics_icons/unknown.svg (scripts/make-badge-icons.py); `colour` is the ring's.
 */
const ringSvg = (colour: string, size: number): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">` +
  "<circle cx=\"32\" cy=\"32\" r=\"32\" fill=\"#FFFFFF\"/>" +
  `<circle cx="32" cy="32" r="29.5" fill="none" stroke="${colour}" stroke-width="4.5" stroke-linecap="round" stroke-dasharray="9 6.446"/>` +
  "</svg>";

export const badgeIconDataUri = (name: string, colour: string): string => {
  const key = `${name}|${colour}`;
  const hit = cache.get(key);
  if (hit) {
    return hit;
  }
  // The "no value" icon is the dashed ring above, at the size of the icons around it (Chris, 2026-10-17: it was far bigger than them). Every other badge is a glyph on a 64px disc.
  const svg =
    name === COMPACT_BADGE
      ? ringSvg(colour, 64)
      : "<svg xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" width=\"64\" height=\"64\" viewBox=\"0 0 64 64\">" +
        "<circle cx=\"32\" cy=\"32\" r=\"32\" fill=\"" + colour + "\"/>" +
        BADGE_ARTWORK[name].replace(/__BG__/g, colour) +
        "</svg>";
  const uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  cache.set(key, uri);

  return uri;
};

// The SAME artwork with no circle, as plain dark line art on a white interior: for places that list several attributes side by side (the Sessions rows, the drawer's breakdown),
// where a coloured circle would only compete with the browser and system icons beside it. Colour there carries no meaning, so there is none (Chris, 2026-10-17).
const PLAIN_GLYPH = "#2D3748";
const plainCache = new Map<string, string>();

export const plainIconDataUri = (name: string): string => {
  const hit = plainCache.get(name);
  if (hit) {
    return hit;
  }
  // the artwork is white strokes/fills on the circle, with __BG__ marking the details that take the circle's colour: dark glyph, white details
  // ("no value" is the same dashed ring as everywhere, in its one slate grey: the dark glyph colour is for glyphs)
  const svg =
    name === COMPACT_BADGE
      ? ringSvg("#8A93A0", 48)
      : "<svg xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" width=\"48\" height=\"48\" viewBox=\"8 8 48 48\">" +
        BADGE_ARTWORK[name].replace(/#FFFFFF/gi, PLAIN_GLYPH).replace(/__BG__/g, "#FFFFFF") +
        "</svg>";
  const uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  plainCache.set(name, uri);

  return uri;
};
